import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';

import HomeComponent from './components/HomeComponent';
import LandingPage from './components/LandingPage';
import LoginComponent from './components/LoginComponent';
import RegisterComponent from './components/RegisterComponent';
import OrdersComponent from './components/OrdersComponent';
import GeneratedBills from './components/GeneratedBills';
import Dashboard from './components/Dashboard';
import Scanner from './components/Scanner';
import Clients from './components/Clients';
import Orders from './components/Orders';
import Bills from './components/Bills';
import OrdersByStage from './components/OrdersByStage'; // ✅ NEW

const AUTH_STORAGE_KEY = 'billing-auth';

function ProtectedRoute({ isAuthenticated, children }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function PublicRoute({ isAuthenticated, children }) {
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

function App() {
  const [authData, setAuthData] = useState(
    JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null')
  );

  const isAuthenticated = Boolean(authData?.token);

  const handleLogin = (loginData) => {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(loginData));
    setAuthData(loginData);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthData(null);
    window.location.replace('/');
  }, []);

  const handleForbiddenLogout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthData(null);

    if (window.location.pathname !== '/') {
      window.location.replace('/');
    }
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 403) {
        handleForbiddenLogout();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [handleForbiddenLogout]);

  return (
    <BrowserRouter>
      <Routes>

        {/* Root */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />}
        />

        {/* Public */}
        <Route
          path="/register"
          element={
            <PublicRoute isAuthenticated={isAuthenticated}>
              <RegisterComponent />
            </PublicRoute>
          }
        />

        <Route
          path="/login"
          element={
            <PublicRoute isAuthenticated={isAuthenticated}>
              <LoginComponent onLogin={handleLogin} />
            </PublicRoute>
          }
        />

        {/* Protected Layout */}
        <Route
          path="/home"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <HomeComponent authData={authData} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="scanner" element={<Scanner />} />
          <Route path="clients" element={<Clients />} />
          <Route path="orders" element={<Orders />} />
          <Route path="bills" element={<Bills />} />

          {/* ✅ NEW ROUTE (IMPORTANT) */}
          <Route path="orders/stage/:stage" element={<OrdersByStage />} />

        </Route>

        {/* Standalone Routes */}
        <Route
          path="/orders/:orderId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <OrdersComponent authData={authData} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/generated-bills"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GeneratedBills />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? '/home' : '/login'} replace />}
        />

      </Routes>
    </BrowserRouter>
  );
}

export default App;
